import React, { useEffect, useRef, useState } from 'react';
import validationAuth from '../../../validationAuth';
import {
    Box, Button, Typography, FormControl, Select, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tabs, Tab, CircularProgress
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ko } from 'date-fns/locale';
import { format, startOfMonth, startOfWeek } from 'date-fns';
import dayjs from 'dayjs';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer
} from 'recharts';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
    getStatisticsSummary,
    getFacilityTypeList,
    getNotCheckedFacilityList,
    getNotCheckedCheckerList,
    getComplaintList,
    getIssueList,
    getForcedFacilityList,
    getForcedCheckerList,
    downloadSummaryExcel,
    downloadNotCheckedExcel,
    downloadComplaintExcel,
    downloadIssueExcel,
    downloadForcedExcel,
} from '../../../api/managerApi';

// ────────────────────────────────────────────────────────────────
// 차트 커스텀 컴포넌트
// ────────────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0];
    return (
        <Box sx={{
            backgroundColor: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            padding: '10px 14px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            minWidth: 100,
        }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', mb: 0.3, fontWeight: 600, letterSpacing: '0.03em' }}>
                {d.payload?.name}
            </Typography>
            <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color: d.payload?.color }}>
                {d.value?.toLocaleString()}
            </Typography>
        </Box>
    );
};

const ChartLabel = ({ x, y, width, value }) => (
    <text x={x + width / 2} y={y - 7} fill="#475569" textAnchor="middle" fontSize={11} fontWeight="700">
        {value?.toLocaleString()}
    </text>
);

// ────────────────────────────────────────────────────────────────
// 임시 데이터
// ────────────────────────────────────────────────────────────────
const MOCK_FACILITY_LIST = [
    { facilityTypeNm: '공중화장실', facilityNm: '아인이공원', tier1Nm: '환경과', tier2Nm: '공원A팀', tier3Nm: 'Axia', checkerNm: '홍길동', uncheckCnt: 10, uncheckRate: '2%', complaintCnt: 3, breakdownCnt: 2, forceRegisterCnt: 1 },
    { facilityTypeNm: '놀이시설', facilityNm: '중앙공원', tier1Nm: '환경과', tier2Nm: '공원B팀', tier3Nm: 'Bxia', checkerNm: '김철수', uncheckCnt: 5, uncheckRate: '1%', complaintCnt: 1, breakdownCnt: 4, forceRegisterCnt: 2 },
    { facilityTypeNm: '운동시설', facilityNm: '한강공원', tier1Nm: '체육과', tier2Nm: '시설팀', tier3Nm: 'Cxia', checkerNm: '이영희', uncheckCnt: 3, uncheckRate: '0.5%', complaintCnt: 2, breakdownCnt: 1, forceRegisterCnt: 0 },
    { facilityTypeNm: '공중화장실', facilityNm: '북한산입구', tier1Nm: '환경과', tier2Nm: '공원A팀', tier3Nm: 'Axia', checkerNm: '박민수', uncheckCnt: 8, uncheckRate: '1.5%', complaintCnt: 0, breakdownCnt: 3, forceRegisterCnt: 4 },
    { facilityTypeNm: '놀이시설', facilityNm: '어린이공원', tier1Nm: '환경과', tier2Nm: '공원C팀', tier3Nm: 'Dxia', checkerNm: '최수진', uncheckCnt: 2, uncheckRate: '0.3%', complaintCnt: 4, breakdownCnt: 0, forceRegisterCnt: 1 },
    { facilityTypeNm: '운동시설', facilityNm: '올림픽공원', tier1Nm: '체육과', tier2Nm: '시설팀', tier3Nm: 'Exia', checkerNm: '정대호', uncheckCnt: 6, uncheckRate: '1.2%', complaintCnt: 2, breakdownCnt: 2, forceRegisterCnt: 3 },
    { facilityTypeNm: '공중화장실', facilityNm: '월드컵공원', tier1Nm: '환경과', tier2Nm: '공원B팀', tier3Nm: 'Bxia', checkerNm: '홍길동', uncheckCnt: 1, uncheckRate: '0.2%', complaintCnt: 1, breakdownCnt: 1, forceRegisterCnt: 0 },
];

const MOCK_CHECKER_LIST = [
    { checkerNm: '홍길동', tier1Nm: '환경과', tier2Nm: '공원A팀', tier3Nm: 'Axia', assignedFacilityCnt: '5개', totalCheckCnt: 50, uncheckCnt: 10, uncheckRate: '20%', forceRegisterCnt: 5 },
    { checkerNm: '김철수', tier1Nm: '환경과', tier2Nm: '공원B팀', tier3Nm: 'Bxia', assignedFacilityCnt: '4개', totalCheckCnt: 40, uncheckCnt: 5, uncheckRate: '12.5%', forceRegisterCnt: 2 },
    { checkerNm: '이영희', tier1Nm: '체육과', tier2Nm: '시설팀', tier3Nm: 'Cxia', assignedFacilityCnt: '6개', totalCheckCnt: 60, uncheckCnt: 3, uncheckRate: '5%', forceRegisterCnt: 1 },
    { checkerNm: '박민수', tier1Nm: '환경과', tier2Nm: '공원A팀', tier3Nm: 'Axia', assignedFacilityCnt: '3개', totalCheckCnt: 30, uncheckCnt: 8, uncheckRate: '26.7%', forceRegisterCnt: 4 },
    { checkerNm: '최수진', tier1Nm: '환경과', tier2Nm: '공원C팀', tier3Nm: 'Dxia', assignedFacilityCnt: '5개', totalCheckCnt: 50, uncheckCnt: 2, uncheckRate: '4%', forceRegisterCnt: 1 },
    { checkerNm: '정대호', tier1Nm: '체육과', tier2Nm: '시설팀', tier3Nm: 'Exia', assignedFacilityCnt: '7개', totalCheckCnt: 70, uncheckCnt: 6, uncheckRate: '8.6%', forceRegisterCnt: 3 },
];

const MOCK_SUMMARY = {
    summary: {
        totalFacilityCnt: 100,
        checkedCnt: 90, checkedPct: 90,
        uncheckedCnt: 10, uncheckedPct: 10,
        complaintCnt: 15,
        breakdownCnt: 20,
        forceRegisterCnt: 30,
    },
    uncheckList: MOCK_FACILITY_LIST,
};

const MOCK_UNCHECK = {
    stats: { totalFacilityCnt: 100, totalUncheckCnt: 1000, avgUncheckCnt: '1.0' },
    facilityList: MOCK_FACILITY_LIST,
    checkerList: MOCK_CHECKER_LIST,
};

const MOCK_COMPLAINT = {
    stats: { totalFacilityCnt: 100, totalComplaintCnt: 20 },
    list: MOCK_FACILITY_LIST,
};

const MOCK_BREAKDOWN = {
    stats: { totalFacilityCnt: 100, totalBreakdownCnt: 20 },
    list: MOCK_FACILITY_LIST,
};

const MOCK_FORCE_REGISTER = {
    stats: { totalFacilityCnt: 100, totalForceRegisterCnt: 20 },
    facilityList: MOCK_FACILITY_LIST,
    checkerList: MOCK_CHECKER_LIST,
};


// ────────────────────────────────────────────────────────────────
// 공통 테이블 컬럼
// ────────────────────────────────────────────────────────────────
// 목 데이터용 (tier*Nm 필드)
const FACILITY_COLS = [
    { id: 'facilityTypeNm', label: '시설유형' },
    { id: 'facilityNm', label: '시설명' },
    { id: 'tier1Nm', label: '총괄부서' },
    { id: 'tier2Nm', label: '관리부서' },
    { id: 'tier3Nm', label: '팀' },
    { id: 'checkerNm', label: '점검자' },
];

const CHECKER_COLS = [
    { id: 'checkerNm', label: '점검자' },
    { id: 'tier1Nm', label: '총괄부서' },
    { id: 'tier2Nm', label: '관리부서' },
    { id: 'tier3Nm', label: '팀' },
    { id: 'assignedFacilityCnt', label: '담당시설' },
    { id: 'totalCheckCnt', label: '총 점검횟수' },
];

// 통계 API 실제 응답 필드용 (top/mid/botDeptNm)
const STATS_FACILITY_COLS = [
    { id: 'facilityTypeNm', label: '시설유형' },
    { id: 'facilityNm', label: '시설명' },
    { id: 'topDeptNm', label: '총괄부서' },
    { id: 'midDeptNm', label: '관리부서' },
    { id: 'botDeptNm', label: '팀' },
    { id: 'checkerNm', label: '점검자' },
];

const STATS_CHECKER_COLS = [
    { id: 'checkerNm', label: '점검자' },
    { id: 'topDeptNm', label: '총괄부서' },
    { id: 'midDeptNm', label: '관리부서' },
    { id: 'botDeptNm', label: '팀' },
    { id: 'facilityCnt', label: '담당시설' },
    { id: 'totalCheckCnt', label: '총 점검횟수' },
];

// 미점검 점검자별 컬럼 (목표점검횟수)
const NOT_CHECKED_CHECKER_COLS = [
    { id: 'checkerNm', label: '점검자' },
    { id: 'topDeptNm', label: '총괄부서' },
    { id: 'midDeptNm', label: '관리부서' },
    { id: 'botDeptNm', label: '팀' },
    { id: 'facilityCnt', label: '담당시설' },
    { id: 'totalCheckCnt', label: '목표점검횟수' },
];

const TABLE_INIT_ROWS = 5;
const TABLE_STEP_ROWS = 10;

const parseNum = (v) => { const n = parseFloat(v ?? 0); return isNaN(n) ? 0 : n; };
const parseRateList = (list) => list.map(item => ({
    ...item,
    notCheckedRate: item.notCheckedRate != null ? parseNum(item.notCheckedRate) : null,
}));

const tableHeaderSx = {
    backgroundColor: '#f5f5f5',
    '& .MuiTableCell-root': { fontWeight: 'bold', fontSize: '0.8rem', padding: '8px 12px' }
};
const tableCellSx = { fontSize: '0.8rem', padding: '8px 12px' };

// ────────────────────────────────────────────────────────────────
// 더보기 테이블
// ────────────────────────────────────────────────────────────────
// maxHeight 지정 시: 고정 높이 + 스크롤 (더보기 없음)
// maxHeight 미지정 시: 기존 더보기/접기 방식
const ExpandableTable = ({ columns, rows = [], extraColumns = [], maxHeight, totalElements, onLoadMore, loadingMore = false }) => {
    const [visibleCount, setVisibleCount] = useState(TABLE_INIT_ROWS);
    const allCols = [...columns, ...extraColumns];

    if (maxHeight) {
        return (
            <TableContainer sx={{ height: maxHeight, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {allCols.map(col => (
                                <TableCell key={col.id} sx={{ ...tableCellSx, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length > 0 ? rows.map((row, i) => (
                            <TableRow key={i} hover>
                                {allCols.map(col => (
                                    <TableCell key={col.id} sx={tableCellSx}>{row[col.id] ?? '-'}</TableCell>
                                ))}
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={allCols.length} align="center" sx={{ ...tableCellSx, color: '#999', py: 3 }}>
                                    데이터가 없습니다.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    const serverTotal = totalElements ?? rows.length;
    const hasMoreLocal = visibleCount < rows.length;
    const hasMoreServer = onLoadMore && rows.length < serverTotal;
    const hasMore = hasMoreLocal || hasMoreServer;
    const isExpanded = visibleCount > TABLE_INIT_ROWS;
    const displayRows = rows.slice(0, visibleCount);

    const handleLoadMore = () => {
        if (hasMoreLocal) {
            setVisibleCount(c => Math.min(c + TABLE_STEP_ROWS, rows.length));
        } else if (hasMoreServer) {
            setVisibleCount(v => v + TABLE_STEP_ROWS);
            onLoadMore();
        }
    };

    return (
        <>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={tableHeaderSx}>
                            {allCols.map(col => (
                                <TableCell key={col.id} sx={tableCellSx}>{col.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayRows.length > 0 ? displayRows.map((row, i) => (
                            <TableRow key={i} hover>
                                {allCols.map(col => (
                                    <TableCell key={col.id} sx={tableCellSx}>{row[col.id] ?? '-'}</TableCell>
                                ))}
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={allCols.length} align="center" sx={{ ...tableCellSx, color: '#999', py: 3 }}>
                                    데이터가 없습니다.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {(hasMore || isExpanded) && (
                <Box display="flex" justifyContent="center" gap={1} mt={1}>
                    {isExpanded && (
                        <Button
                            size="small"
                            endIcon={<KeyboardArrowDownIcon sx={{ transform: 'rotate(180deg)' }} />}
                            onClick={() => setVisibleCount(TABLE_INIT_ROWS)}
                            sx={{ color: '#555', fontSize: '0.8rem' }}
                        >
                            접기
                        </Button>
                    )}
                    {hasMore && (
                        <Button
                            size="small"
                            endIcon={loadingMore ? null : <KeyboardArrowDownIcon />}
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                            sx={{ color: '#555', fontSize: '0.8rem' }}
                        >
                            {loadingMore ? <CircularProgress size={14} color="inherit" /> : '펼치기'}
                        </Button>
                    )}
                </Box>
            )}
        </>
    );
};

// ────────────────────────────────────────────────────────────────
// 통계 카드
// ────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, unit = '', percentage, dotColor, sx = {} }) => (
    <Box sx={{
        borderRadius: '12px',
        border: '1px solid #e8edf2',
        padding: '14px 16px',
        backgroundColor: '#fff',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: '0 4px 14px rgba(0,0,0,0.08)' },
        ...sx
    }}>
        <Box display="flex" alignItems="center" gap={0.8} mb={0.8}>
            <Box sx={{
                width: 6, height: 6, borderRadius: '50%',
                backgroundColor: dotColor || '#555',
                flexShrink: 0,
                boxShadow: `0 0 0 3px ${dotColor}22`,
            }} />
            <Typography sx={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, letterSpacing: '0.02em' }}>
                {title}
            </Typography>
        </Box>
        <Box display="flex" alignItems="baseline" sx={{ justifyContent: 'space-between' }}>
            <Box display="flex" alignItems="baseline" gap={0.8}>
                <Typography sx={{ fontSize: '1.9rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>
                    {(value ?? 0).toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>{unit}</Typography>
            </Box>
            {percentage !== undefined && (
                <Typography sx={{ fontSize: '0.78rem', color: dotColor, fontWeight: 700 }}>{percentage}%</Typography>
            )}
        </Box>
    </Box>
);

// ────────────────────────────────────────────────────────────────
// 탭 0 – 통계 요약
// ────────────────────────────────────────────────────────────────
const SummaryTab = ({ data, listMeta = {}, onLoadMore = {} }) => {
    const summary = data?.summary || {};
    const uncheckList = data?.uncheckList || [];

    const chartData = [
        { name: '점검대상', value: summary.totalCount ?? 0, color: '#3b82f6', gradId: 'g0' },
        { name: '점검완료', value: summary.checkedCount ?? 0, color: '#06b6d4', gradId: 'g1' },
        { name: '미점검', value: summary.notCheckedCount ?? 0, color: '#f43f5e', gradId: 'g2' },
        { name: '고객의소리', value: summary.complaintCount ?? 0, color: '#10b981', gradId: 'g3' },
        { name: '고장A/S', value: summary.issueCount ?? 0, color: '#f97316', gradId: 'g4' },
        { name: '점검강제등록', value: summary.forcedCount ?? 0, color: '#a855f7', gradId: 'g5' },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" gap={2} mb={2} sx={{ flexShrink: 0 }}>
                {/* 좌측 카드 그리드 */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, width: '380px', flexShrink: 0 }}>
                    <StatCard title="점검대상" value={summary.totalCount} unit="개 시설" dotColor="#3b82f6" />
                    <StatCard title="고객의 소리" value={summary.complaintCount} unit="건" dotColor="#10b981" />
                    <StatCard title="점검완료" value={summary.checkedCount} unit="개 시설" percentage={summary.checkedRate} dotColor="#06b6d4" />
                    <StatCard title="고장 A/S" value={summary.issueCount} unit="건" dotColor="#f97316" />
                    <StatCard title="미점검" value={summary.notCheckedCount} unit="개 시설" percentage={summary.notCheckedRate} dotColor="#f43f5e" />
                    <StatCard title="점검강제등록" value={summary.forcedCount} unit="개 시설" dotColor="#a855f7" />
                </Box>

                {/* 우측 바 차트 */}
                <Box sx={{
                    flex: 1,
                    borderRadius: '14px',
                    background: 'linear-gradient(160deg, #f8fafc 0%, #f1f5f9 100%)',
                    border: '1px solid #e2e8f0',
                    p: 2.5,
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                        <Box>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#1e293b' }}>점검 현황 분석</Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.2 }}>조회 기간 기준</Typography>
                        </Box>
                        {/* 범례 */}
                        <Box display="flex" flexWrap="wrap" gap={1} justifyContent="flex-end" sx={{ maxWidth: '55%' }}>
                            {chartData.map(d => (
                                <Box key={d.gradId} display="flex" alignItems="center" gap={0.5}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: d.color }} />
                                    <Typography sx={{ fontSize: '0.68rem', color: '#64748b' }}>{d.name}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={chartData} margin={{ top: 22, right: 8, left: -28, bottom: 0 }} barCategoryGap="38%">
                            <defs>
                                {chartData.map(d => (
                                    <linearGradient key={d.gradId} id={d.gradId} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={d.color} stopOpacity={1} />
                                        <stop offset="100%" stopColor={d.color} stopOpacity={0.45} />
                                    </linearGradient>
                                ))}
                            </defs>
                            <CartesianGrid strokeDasharray="4 4" stroke="#e2e8f0" vertical={false} />
                            <XAxis
                                dataKey="name"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: '#64748b', fontWeight: 500 }}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 10, fill: '#94a3b8' }}
                                width={36}
                            />
                            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(15,23,42,0.04)', radius: 6 }} />
                            <Bar dataKey="value" radius={[7, 7, 0, 0]} label={<ChartLabel />}>
                                {chartData.map((d, i) => (
                                    <Cell key={i} fill={`url(#${d.gradId})`} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </Box>

            {/* 미점검 현황 테이블 */}
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '10px', p: 2 }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem', mb: 1.5 }}>미점검 현황</Typography>
                <ExpandableTable
                    columns={STATS_FACILITY_COLS}
                    rows={uncheckList}
                    extraColumns={[
                        { id: 'notCheckedCnt', label: '미점검횟수' },
                        { id: 'notCheckedRate', label: '미점검률' },
                    ]}
                    totalElements={listMeta['summary.uncheck']?.totalElements}
                    onLoadMore={onLoadMore['summary.uncheck']}
                    loadingMore={listMeta['summary.uncheck']?.loading}
                />
            </Box>
        </Box>
    );
};

// ────────────────────────────────────────────────────────────────
// 탭 1 – 미점검 분석
// ────────────────────────────────────────────────────────────────
const UncheckTab = ({ data, listMeta = {}, onLoadMore = {} }) => {
    const stats = data?.stats || {};
    const facilityList = data?.facilityList || [];
    const checkerList = data?.checkerList || [];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" gap={1.5} mb={2} sx={{ flexShrink: 0 }}>
                <StatCard title="점검대상시설" value={stats.totalFacilityCnt} unit="개 시설" dotColor="#333" sx={{ flex: 1 }} />
                <StatCard title="총 미점검횟수" value={stats.totalUncheckCnt} unit="회" dotColor="#1565C0" sx={{ flex: 1 }} />
                <StatCard title="평균 미점검횟수" value={stats.avgUncheckCnt} unit="회/시설" dotColor="#8E24AA" sx={{ flex: 1 }} />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '10px', p: 2 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem', mb: 1.5 }}>
                        미점검현황 <span style={{ color: '#777', fontWeight: 'normal', fontSize: '0.85rem' }}>시설별</span>
                    </Typography>
                    <ExpandableTable
                        columns={STATS_FACILITY_COLS}
                        rows={facilityList}
                        extraColumns={[
                            { id: 'notCheckedCnt', label: '미점검횟수' },
                            { id: 'notCheckedRate', label: '미점검률' },
                        ]}
                        totalElements={listMeta['uncheck.facility']?.totalElements}
                        onLoadMore={onLoadMore['uncheck.facility']}
                        loadingMore={listMeta['uncheck.facility']?.loading}
                    />
                </Box>

                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '10px', p: 2 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem', mb: 1.5 }}>
                        미점검현황 <span style={{ color: '#777', fontWeight: 'normal', fontSize: '0.85rem' }}>점검자별</span>
                    </Typography>
                    <ExpandableTable
                        columns={NOT_CHECKED_CHECKER_COLS}
                        rows={checkerList}
                        extraColumns={[
                            { id: 'notCheckedCnt', label: '미점검횟수' },
                            { id: 'notCheckedRate', label: '미점검률' },
                        ]}
                        totalElements={listMeta['uncheck.checker']?.totalElements}
                        onLoadMore={onLoadMore['uncheck.checker']}
                        loadingMore={listMeta['uncheck.checker']?.loading}
                    />
                </Box>
            </Box>
        </Box>
    );
};

// ────────────────────────────────────────────────────────────────
// 탭 2 – 고객의소리 분석
// ────────────────────────────────────────────────────────────────
const ComplaintTab = ({ data, listMeta = {}, onLoadMore = {} }) => {
    const stats = data?.stats || {};
    const list = data?.list || [];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" gap={1.5} mb={2} sx={{ flexShrink: 0 }}>
                <StatCard title="점검대상시설" value={stats.totalFacilityCnt} unit="개 시설" dotColor="#333" sx={{ maxWidth: 240 }} />
                <StatCard title="총 고객의소리 접수 현황" value={stats.totalComplaintCnt} unit="회" dotColor="#43A047" sx={{ maxWidth: 240 }} />
            </Box>

            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '10px', p: 2 }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem', mb: 1.5 }}>고객의소리 접수 현황</Typography>
                <ExpandableTable
                    columns={STATS_FACILITY_COLS}
                    rows={list}
                    extraColumns={[{ id: 'complaintCnt', label: '접수횟수' }]}
                    totalElements={listMeta['complaint.list']?.totalElements}
                    onLoadMore={onLoadMore['complaint.list']}
                    loadingMore={listMeta['complaint.list']?.loading}
                />
            </Box>
        </Box>
    );
};

// ────────────────────────────────────────────────────────────────
// 탭 3 – 고장A/S 분석
// ────────────────────────────────────────────────────────────────
const BreakdownTab = ({ data, listMeta = {}, onLoadMore = {} }) => {
    const stats = data?.stats || {};
    const list = data?.list || [];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" gap={1.5} mb={2} sx={{ flexShrink: 0 }}>
                <StatCard title="점검대상시설" value={stats.totalFacilityCnt} unit="개 시설" dotColor="#333" sx={{ maxWidth: 240 }} />
                <StatCard title="총 고장A/S 접수 현황" value={stats.totalBreakdownCnt} unit="회" dotColor="#FB8C00" sx={{ maxWidth: 240 }} />
            </Box>

            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '10px', p: 2 }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem', mb: 1.5 }}>고장 A/S 접수 현황</Typography>
                <ExpandableTable
                    columns={STATS_FACILITY_COLS}
                    rows={list}
                    extraColumns={[{ id: 'issueCnt', label: '접수횟수' }]}
                    totalElements={listMeta['breakdown.list']?.totalElements}
                    onLoadMore={onLoadMore['breakdown.list']}
                    loadingMore={listMeta['breakdown.list']?.loading}
                />
            </Box>
        </Box>
    );
};

// ────────────────────────────────────────────────────────────────
// 탭 4 – 점검강제등록 분석
// ────────────────────────────────────────────────────────────────
const ForceRegisterTab = ({ data, listMeta = {}, onLoadMore = {} }) => {
    const stats = data?.stats || {};
    const facilityList = data?.facilityList || [];
    const checkerList = data?.checkerList || [];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" gap={1.5} mb={2} sx={{ flexShrink: 0 }}>
                <StatCard title="점검대상시설" value={stats.totalFacilityCnt} unit="개 시설" dotColor="#333" sx={{ maxWidth: 240 }} />
                <StatCard title="총 점검강제등록 현황" value={stats.totalForceRegisterCnt} unit="회" dotColor="#8E24AA" sx={{ maxWidth: 240 }} />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '10px', p: 2 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem', mb: 1.5 }}>
                        점검강제등록 현황 <span style={{ color: '#777', fontWeight: 'normal', fontSize: '0.85rem' }}>시설별</span>
                    </Typography>
                    <ExpandableTable
                        columns={STATS_FACILITY_COLS}
                        rows={facilityList}
                        extraColumns={[{ id: 'forcedCnt', label: '강제등록횟수' }]}
                        totalElements={listMeta['forced.facility']?.totalElements}
                        onLoadMore={onLoadMore['forced.facility']}
                        loadingMore={listMeta['forced.facility']?.loading}
                    />
                </Box>

                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '10px', p: 2 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem', mb: 1.5 }}>
                        점검강제등록 현황 <span style={{ color: '#777', fontWeight: 'normal', fontSize: '0.85rem' }}>점검자별</span>
                    </Typography>
                    <ExpandableTable
                        columns={STATS_CHECKER_COLS}
                        rows={checkerList}
                        extraColumns={[{ id: 'forcedCnt', label: '강제등록횟수' }]}
                        totalElements={listMeta['forced.checker']?.totalElements}
                        onLoadMore={onLoadMore['forced.checker']}
                        loadingMore={listMeta['forced.checker']?.loading}
                    />
                </Box>
            </Box>
        </Box>
    );
};

// ────────────────────────────────────────────────────────────────
// 메인 컴포넌트
// ────────────────────────────────────────────────────────────────
const DashBoardFacility = () => {
    const today = format(new Date(), 'yyyy-MM-dd');

    const [activeTab, setActiveTab] = useState(0);
    const [periodType, setPeriodType] = useState('today');
    const [customStart, setCustomStart] = useState(today);
    const [customEnd, setCustomEnd] = useState(today);
    const [facilityTypeId, setFacilityTypeId] = useState('');
    const [facilityTypes, setFacilityTypes] = useState([]);
    const [loading, setLoading] = useState(false);

    const [summaryData, setSummaryData] = useState(null);
    const [uncheckData, setUncheckData] = useState(null);
    const [complaintData, setComplaintData] = useState(null);
    const [breakdownData, setBreakdownData] = useState(null);
    const [forceRegisterData, setForceRegisterData] = useState(null);

    // 리스트별 페이지네이션 메타 (page, totalPages, totalElements, loading)
    const [listMeta, setListMeta] = useState({});
    const listMetaRef = useRef({});

    const updateMeta = (key, meta) => {
        const next = { ...listMetaRef.current, [key]: meta };
        listMetaRef.current = next;
        setListMeta(next);
    };

    const getDateRange = () => {
        const now = new Date();
        switch (periodType) {
            case 'today':
                return { startDate: today, endDate: today };
            case 'weekly':
                return {
                    startDate: format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
                    endDate: today,
                };
            case 'monthly':
                return { startDate: format(startOfMonth(now), 'yyyy-MM-dd'), endDate: today };
            case 'custom':
                return { startDate: customStart, endDate: customEnd };
            default:
                return { startDate: today, endDate: today };
        }
    };

    // Spring Page 및 일반 배열/객체 응답 모두 처리
    const extractList = (data) =>
        Array.isArray(data) ? data : (data?.content ?? data?.list ?? data?.data ?? []);
    const extractTotal = (data) =>
        data?.totalElements ?? data?.totalCount ?? (Array.isArray(data) ? data.length : 0);
    // sentPage: 실제 API에 보낸 1-based 페이지 번호
    const extractPageMeta = (resData, sentPage) => ({
        page: sentPage,
        totalPages: resData?.totalPages ?? 1,
        totalElements: resData?.totalElements ?? (Array.isArray(resData) ? resData.length : 0),
        loading: false,
    });

    const loadTabData = async (tab) => {
        setLoading(true);
        // 탭 전환 시 메타 초기화
        listMetaRef.current = {};
        setListMeta({});
        const { startDate, endDate } = getDateRange();
        // 항상 page=1, size=10 명시 (1-based)
        const params = {
            startDate,
            endDate,
            page: 1,
            size: 10,
            ...(facilityTypeId ? { facilityTypeId } : {}),
        };

        try {
            switch (tab) {
                case 0: {
                    const [summaryRes, facilityRes] = await Promise.all([
                        getStatisticsSummary(params),
                        getNotCheckedFacilityList(params),
                    ]);
                    setSummaryData({
                        summary: summaryRes.data,
                        uncheckList: parseRateList(extractList(facilityRes.data)),
                    });
                    updateMeta('summary.uncheck', extractPageMeta(facilityRes.data, 1));
                    break;
                }
                case 1: {
                    const [summaryRes, facilityRes, checkerRes] = await Promise.all([
                        getStatisticsSummary(params),
                        getNotCheckedFacilityList(params),
                        getNotCheckedCheckerList(params),
                    ]);
                    setUncheckData({
                        stats: {
                            totalFacilityCnt: summaryRes.data?.totalCount ?? 0,
                            totalUncheckCnt: summaryRes.data?.notCheckedCount ?? 0,
                            avgUncheckCnt: parseNum(summaryRes.data?.avgNotCheckedCnt),
                        },
                        facilityList: parseRateList(extractList(facilityRes.data)),
                        checkerList: parseRateList(extractList(checkerRes.data)),
                    });
                    updateMeta('uncheck.facility', extractPageMeta(facilityRes.data, 1));
                    updateMeta('uncheck.checker', extractPageMeta(checkerRes.data, 1));
                    break;
                }
                case 2: {
                    const [summaryRes, listRes] = await Promise.all([
                        getStatisticsSummary(params),
                        getComplaintList(params),
                    ]);
                    setComplaintData({
                        stats: {
                            totalFacilityCnt: summaryRes.data?.totalCount ?? 0,
                            totalComplaintCnt: summaryRes.data?.complaintCount ?? extractTotal(listRes.data),
                        },
                        list: extractList(listRes.data),
                    });
                    updateMeta('complaint.list', extractPageMeta(listRes.data, 1));
                    break;
                }
                case 3: {
                    const [summaryRes, listRes] = await Promise.all([
                        getStatisticsSummary(params),
                        getIssueList(params),
                    ]);
                    setBreakdownData({
                        stats: {
                            totalFacilityCnt: summaryRes.data?.totalCount ?? 0,
                            totalBreakdownCnt: summaryRes.data?.issueCount ?? extractTotal(listRes.data),
                        },
                        list: extractList(listRes.data),
                    });
                    updateMeta('breakdown.list', extractPageMeta(listRes.data, 1));
                    break;
                }
                case 4: {
                    const [summaryRes, facilityRes, checkerRes] = await Promise.all([
                        getStatisticsSummary(params),
                        getForcedFacilityList(params),
                        getForcedCheckerList(params),
                    ]);
                    setForceRegisterData({
                        stats: {
                            totalFacilityCnt: summaryRes.data?.totalCount ?? 0,
                            totalForceRegisterCnt: summaryRes.data?.forcedCount ?? extractTotal(facilityRes.data),
                        },
                        facilityList: extractList(facilityRes.data),
                        checkerList: extractList(checkerRes.data),
                    });
                    updateMeta('forced.facility', extractPageMeta(facilityRes.data, 1));
                    updateMeta('forced.checker', extractPageMeta(checkerRes.data, 1));
                    break;
                }
                default:
                    break;
            }
        } catch (e) {
            console.error('데이터 조회 실패', e);
        } finally {
            setLoading(false);
        }
    };

    // 다음 페이지 로드 (1-based 페이지 번호)
    const loadMore = async (key, apiFn, dataSetter, rowKey, transform = x => x) => {
        const meta = listMetaRef.current[key] || {};
        const nextPage = (meta.page ?? 1) + 1;
        if (nextPage > (meta.totalPages ?? 1)) return;

        updateMeta(key, { ...meta, loading: true });
        try {
            const { startDate, endDate } = getDateRange();
            const params = { startDate, endDate, page: nextPage, size: 10, ...(facilityTypeId ? { facilityTypeId } : {}) };
            const res = await apiFn(params);
            const newRows = transform(extractList(res.data));
            dataSetter(prev => ({ ...prev, [rowKey]: [...(prev?.[rowKey] || []), ...newRows] }));
            updateMeta(key, extractPageMeta(res.data, nextPage));
        } catch (e) {
            console.error('추가 로드 실패', e);
            updateMeta(key, { ...listMetaRef.current[key], loading: false });
        }
    };

    useEffect(() => {
        const loadFacilityTypes = async () => {
            try {
                const res = await getFacilityTypeList();
                setFacilityTypes(res.data || []);
            } catch (e) {
                console.error('시설유형 조회 실패', e);
            }
        };
        loadFacilityTypes();
        loadTabData(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTabChange = (_, newTab) => {
        setActiveTab(newTab);
        loadTabData(newTab);
    };

    const handleSearch = () => loadTabData(activeTab);

    const handleExcelDownload = async () => {
        const { startDate, endDate } = getDateRange();
        const params = {
            startDate,
            endDate,
            ...(facilityTypeId ? { facilityTypeId } : {}),
        };
        const downloadFns = [
            downloadSummaryExcel,
            downloadNotCheckedExcel,
            downloadComplaintExcel,
            downloadIssueExcel,
            downloadForcedExcel,
        ];
        const fileNames = [
            '통계요약',
            '미점검분석',
            '고객의소리분석',
            '고장AS분석',
            '점검강제등록분석',
        ];
        try {
            const res = await downloadFns[activeTab](params);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `${fileNames[activeTab]}_${startDate}_${endDate}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('엑셀 다운로드 실패', e);
        }
    };

    const periodBtnSx = (type) => ({
        minWidth: '48px',
        height: '36px',
        fontSize: '0.8rem',
        boxShadow: 'none',
        backgroundColor: periodType === type ? '#1565C0' : '#fff',
        color: periodType === type ? '#fff' : '#333',
        border: '1px solid #ccc',
        '&:hover': { backgroundColor: periodType === type ? '#1565C0' : '#f5f5f5', boxShadow: 'none' }
    });

    const tabLabels = ['통계 요약', '미점검 분석', '고객의소리 분석', '고장A/S 분석', '점검강제등록 분석'];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
            <Box>
                <Box sx={{
                    backgroundColor: '#fff',
                    borderRadius: '10px',
                    border: '1px solid #e0e0e0',
                    overflow: 'hidden',
                    // 제목 제거 후 확보된 높이 반영
                    height: 'calc(100vh - 120px)',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    {/* 탭 */}
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            flexShrink: 0,
                            borderBottom: '1px solid #e0e0e0',
                            '& .MuiTab-root': { fontSize: '0.9rem', minHeight: '52px' },
                            '& .Mui-selected': { color: '#1565C0', fontWeight: 'bold' },
                            '& .MuiTabs-indicator': { backgroundColor: '#1565C0' }
                        }}
                    >
                        {tabLabels.map((label, i) => <Tab key={i} label={label} />)}
                    </Tabs>

                    {/* 필터 바 */}
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ px: 2, py: 2, borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                                value={facilityTypeId}
                                onChange={e => setFacilityTypeId(e.target.value)}
                                displayEmpty
                                sx={{ fontSize: '0.8rem', height: '36px' }}
                            >
                                <MenuItem value="">전체</MenuItem>
                                {facilityTypes.map(ft => (
                                    <MenuItem key={ft.facilityTypeId} value={ft.facilityTypeId}>
                                        {ft.facilityTypeNm}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button variant="contained" sx={periodBtnSx('today')} onClick={() => setPeriodType('today')}>오늘</Button>
                        <Button variant="contained" sx={periodBtnSx('weekly')} onClick={() => setPeriodType('weekly')}>주간</Button>
                        <Button variant="contained" sx={periodBtnSx('monthly')} onClick={() => setPeriodType('monthly')}>월간</Button>

                        <Button
                            variant="contained"
                            startIcon={<CalendarTodayIcon sx={{ fontSize: '0.85rem' }} />}
                            sx={{ ...periodBtnSx('custom'), minWidth: '90px' }}
                            onClick={() => setPeriodType('custom')}
                        >
                            기간선택
                        </Button>

                        {periodType === 'custom' && (
                            <>
                                <DatePicker
                                    value={new Date(customStart)}
                                    onChange={v => v && setCustomStart(format(v, 'yyyy-MM-dd'))}
                                    format="yyyy-MM-dd"
                                    minDate={dayjs('2020-01-01')}
                                    maxDate={dayjs(customEnd)}
                                    slotProps={{ textField: { size: 'small', sx: { width: 140, '& .MuiOutlinedInput-root': { height: '36px', fontSize: '0.8rem' } } } }}
                                />
                                <Typography sx={{ fontSize: '0.8rem', color: '#888' }}>~</Typography>
                                <DatePicker
                                    value={new Date(customEnd)}
                                    onChange={v => v && setCustomEnd(format(v, 'yyyy-MM-dd'))}
                                    format="yyyy-MM-dd"
                                    minDate={dayjs(customStart)}
                                    maxDate={dayjs()}
                                    slotProps={{ textField: { size: 'small', sx: { width: 140, '& .MuiOutlinedInput-root': { height: '36px', fontSize: '0.8rem' } } } }}
                                />
                            </>
                        )}

                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            disabled={loading}
                            sx={{
                                height: '36px', fontSize: '0.8rem', boxShadow: 'none', minWidth: '64px',
                                backgroundColor: '#1565C0',
                                '&:hover': { backgroundColor: '#1565C0', boxShadow: 'none' }
                            }}
                        >
                            {loading ? <CircularProgress size={14} color="inherit" /> : '검색'}
                        </Button>

                        <Button
                            variant="contained"
                            onClick={handleExcelDownload}
                            sx={{
                                height: '36px', fontSize: '0.8rem', boxShadow: 'none', minWidth: '100px',
                                backgroundColor: '#2e7d32',
                                '&:hover': { backgroundColor: '#2e7d32', boxShadow: 'none' }
                            }}
                        >
                            엑셀 다운로드
                        </Button>
                    </Box>

                    {/* 탭 콘텐츠 – 남은 높이를 채우고 내부 스크롤 */}
                    <Box sx={{ p: 2, flex: 1, overflow: 'auto', minHeight: 0 }}>
                        {activeTab === 0 && (
                            <SummaryTab
                                data={summaryData}
                                listMeta={listMeta}
                                onLoadMore={{
                                    'summary.uncheck': () => loadMore('summary.uncheck', getNotCheckedFacilityList, setSummaryData, 'uncheckList', parseRateList),
                                }}
                            />
                        )}
                        {activeTab === 1 && (
                            <UncheckTab
                                data={uncheckData}
                                listMeta={listMeta}
                                onLoadMore={{
                                    'uncheck.facility': () => loadMore('uncheck.facility', getNotCheckedFacilityList, setUncheckData, 'facilityList', parseRateList),
                                    'uncheck.checker': () => loadMore('uncheck.checker', getNotCheckedCheckerList, setUncheckData, 'checkerList', parseRateList),
                                }}
                            />
                        )}
                        {activeTab === 2 && (
                            <ComplaintTab
                                data={complaintData}
                                listMeta={listMeta}
                                onLoadMore={{
                                    'complaint.list': () => loadMore('complaint.list', getComplaintList, setComplaintData, 'list'),
                                }}
                            />
                        )}
                        {activeTab === 3 && (
                            <BreakdownTab
                                data={breakdownData}
                                listMeta={listMeta}
                                onLoadMore={{
                                    'breakdown.list': () => loadMore('breakdown.list', getIssueList, setBreakdownData, 'list'),
                                }}
                            />
                        )}
                        {activeTab === 4 && (
                            <ForceRegisterTab
                                data={forceRegisterData}
                                listMeta={listMeta}
                                onLoadMore={{
                                    'forced.facility': () => loadMore('forced.facility', getForcedFacilityList, setForceRegisterData, 'facilityList'),
                                    'forced.checker': () => loadMore('forced.checker', getForcedCheckerList, setForceRegisterData, 'checkerList'),
                                }}
                            />
                        )}
                    </Box>
                </Box>
            </Box>
        </LocalizationProvider>
    );
};

export default validationAuth(DashBoardFacility);
